import { useCallback, useEffect, useState } from 'react'
import { applyEdgeChanges, applyNodeChanges, useReactFlow } from '@xyflow/react'
import { getAddNodeMutations } from '../../helpers'
import { useJourneyContext } from '../../context'
import { AddStepType, JourneyEdge, JourneyNode } from '../../types/journey-nodes'
import type { IActionStep, IDelayStep } from '@pushly/models/lib/structs/journeys'
import { CopyTypes, JourneyAction } from '../../types/journey-context'
import { useAddStepOptions } from '../../hooks/use-add-step-options'

export interface UseEdgeActionsParams {
    source: string
    target: string
    variantId?: string
}

export interface UseEdgeActionsReturn {
    edgeActionOptions: AddStepType[]
    showEdgeActions: boolean
    setShowEdgeActions: (show: boolean) => void
    handleAddNode: (
        stepType: AddStepType,
        addConfig?: {
            override?: IActionStep | IDelayStep
            overrideType?: CopyTypes
            skipOpenEditor?: boolean
        },
    ) => void
}

export function useEdgeActions({ source, target, variantId }: UseEdgeActionsParams): UseEdgeActionsReturn {
    const [state, dispatch] = useJourneyContext()
    const { getNode, getNodes, getEdges, setNodes, setEdges } = useReactFlow<JourneyNode>()

    const edgeActionOptions = useAddStepOptions(source)
    const [showEdgeActions, setShowEdgeActions] = useState(false)

    // Watch for close popover signal
    useEffect(() => {
        if (state.closePopoversSignal > 0) {
            setShowEdgeActions(false)
        }
    }, [state.closePopoversSignal])

    const handleAddNode = useCallback(
        (
            stepType: AddStepType,
            addConfig: { override?: IActionStep | IDelayStep; overrideType?: CopyTypes; skipOpenEditor?: boolean } = {},
        ) => {
            setShowEdgeActions(false)

            const sourceNode = getNode(source)
            const targetNode = getNode(target)

            if (sourceNode && targetNode) {
                const [nodeChanges, edgeChanges, newNode] = getAddNodeMutations(
                    stepType,
                    getNodes(),
                    getNode(source)!,
                    getNode(target)!,
                    addConfig.override,
                    addConfig.overrideType,
                    variantId,
                    getEdges() as JourneyEdge[],
                )

                setNodes((nodes) => applyNodeChanges(nodeChanges, nodes))
                setEdges((edges) => applyEdgeChanges(edgeChanges, edges))

                if (!addConfig.skipOpenEditor) {
                    if (newNode) {
                        dispatch({ action: JourneyAction.ON_NODE_ADD, data: newNode })
                    }
                }
            }
        },
        [source, target],
    )

    return {
        edgeActionOptions,
        showEdgeActions,
        setShowEdgeActions,
        handleAddNode,
    }
}
